import { ChevronDown, Eye, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DEFAULT_VISIBLE_COLUMNS, EXTRA_COLUMNS, COLUMN_LABELS } from '../documents.constants'
import { DocumentColumnKey, TagFacet, DOCUMENT_CATEGORIES } from '../documents.types'
import { Folder } from '../collections.types'
import { TagFacetSelector } from './tag-facet-selector'
import { CollectionsFilterSelect } from './collections-filter-select'

type DocumentsToolbarProps = {
  search: string
  onSearchChange: (search: string) => void
  category: string
  onCategoryChange: (category: string) => void
  tags: TagFacet[]
  selectedTags: string[]
  onTagsChange: (tags: string[]) => void
  tagsLoading?: boolean
  visibleColumns: DocumentColumnKey[]
  onVisibleColumnsChange: (columns: DocumentColumnKey[]) => void
  onOpenFilters: () => void
  activeFilterCount: number
  // Only needed on narrow screens, where the Collections sidebar is hidden in favour of this filter.
  collectionsFilter: {
    folders: Folder[]
    selectedFolderId: string | null
    onSelect: (folderId: string | null) => void
  }
}

export const DocumentsToolbar = ({
  search,
  onSearchChange,
  category,
  onCategoryChange,
  tags,
  selectedTags,
  onTagsChange,
  tagsLoading,
  visibleColumns,
  onVisibleColumnsChange,
  onOpenFilters,
  activeFilterCount,
  collectionsFilter,
}: DocumentsToolbarProps) => {
  const toggleColumn = (column: DocumentColumnKey, checked: boolean) => {
    onVisibleColumnsChange(
      checked ? [...visibleColumns, column] : visibleColumns.filter((c) => c !== column),
    )
  }

  const allColumns = [...DEFAULT_VISIBLE_COLUMNS, ...EXTRA_COLUMNS]

  return (
    <div className="flex flex-wrap items-center gap-2 justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <div className="xl:hidden">
          <CollectionsFilterSelect
            folders={collectionsFilter.folders}
            selectedFolderId={collectionsFilter.selectedFolderId}
            onSelect={collectionsFilter.onSelect}
          />
        </div>

        <Select value={category} onValueChange={onCategoryChange}>
          <SelectTrigger className="h-10 w-[160px] rounded-lg bg-background text-sm shadow-sm border border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {DOCUMENT_CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <TagFacetSelector
          tags={tags}
          selected={selectedTags}
          onChange={onTagsChange}
          isLoading={tagsLoading}
        />

        <Input
          placeholder="Search documents"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-10 w-[260px] rounded-lg bg-background text-sm shadow-sm border border-border"
        />
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-10 min-w-52 items-center justify-between gap-3 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
            >
              <span className="inline-flex items-center gap-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                Columns
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Show columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              onClick={() => onVisibleColumnsChange(allColumns)}
            >
              Select all
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              onClick={() => onVisibleColumnsChange([])}
            >
              Clear all
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {allColumns.map((column) => (
              <DropdownMenuItem
                key={column}
                onSelect={(e) => e.preventDefault()}
                onClick={() => toggleColumn(column, !visibleColumns.includes(column))}
              >
                <Checkbox
                  checked={visibleColumns.includes(column)}
                  aria-label={COLUMN_LABELS[column]}
                  className="pointer-events-none border border-border bg-background text-primary data-[state=checked]:border-primary data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary"
                />
                {COLUMN_LABELS[column]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="outline" onClick={onOpenFilters}>
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="default" size="xs" className="rounded-full px-1.5">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </div>
    </div>
  )
}
